import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { OpticalRotationDatum } from '../models/optical-rotation-datum';
import { BenchSheetSampleType, BenchSheetTechnologyType, EditType, MessageBoxReplyType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { Button } from 'devextreme-react/button';
import Guid from 'devextreme/core/guid';
import { MessageBoxReply } from '../models/message-box-reply';

import { ReactGrid, Id, Cell, CellChange, TextCell, Column, Row, Range, DefaultCellTypes } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";

const PENDING_CHANGES = 1;

const DECIMAL_PLACES = 3;
const SOLVENT_ID_COLUMN_IDX = 0;
const EDIT_TYPE_COLUMN_ID = "editType";

export interface Props {
    benchSheetId?: string;
    onClose?: () => void;
}

export default function OpticalRotationDataComponent(props: Props) {
    const [opticalRotationData, setOpticalRotationData] =
        useState<OpticalRotationDatum[]>();

    const [selectedOpticalRotationDatumId, setSelectedOpticalRotationDatumId] =
        useState<string>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [rows, setRows] =
        useState<Row[]>();

    const [headerRow, setHeaderRow] =
        useState<Row>();

    const [columns, setColumns] =
        useState<Column[]>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    useEffect(() => {
        getRows();
    }, [opticalRotationData]);

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button className="title-bar-button"
                        text="Add Row" icon="plus" type="success"
                        hint="add row"
                        onClick={onAddRowClick} />

                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>
            </div>

            <div className="pw-margin-top-1">
                <ReactGrid
                    enableFillHandle={true}
                    enableRangeSelection={true}
                    rows={rows ? rows : []} columns={columns ? columns : []}
                    onSelectionChanging={onSelectionChanging}
                    onColumnResized={handleColumnResize}
                    onCellsChanged={handleChanges} />
            </div>
            
            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onReply={onGlobalDialogReply} onClose={onGlobalDialogClose} />
            }

        </div>
    );

    function handleColumnResize(ci: Id, width: number) {
        if (!columns)
            return;

        setColumns((prevColumns) => {
            const columnIndex = prevColumns.findIndex(el => el.columnId === ci);
            const resizedColumn = prevColumns[columnIndex];
            const updatedColumn = { ...resizedColumn, width };
            prevColumns[columnIndex] = updatedColumn;
            return [...prevColumns];
        });
    }

    function onAddRowClick() {
        let editedOpticalRotationData = [...opticalRotationData];

        let newOpticalRotationDatum: OpticalRotationDatum = {
            sampleName: "",
            opticalRotationDatumId: new Guid().toString(),
            editType: EditType.Insert
        };

        editedOpticalRotationData.push(newOpticalRotationDatum);

        setOpticalRotationData(editedOpticalRotationData);
    }

    function onSelectionChanging(selectedRanges: Range[]): boolean {
        console.log("onSelectionChanging");
        console.log(selectedRanges);

        if (selectedRanges && selectedRanges.length > 0 &&
            selectedRanges[0].columns && selectedRanges[0].columns.length > 0 &&
            selectedRanges[0].rows && selectedRanges[0].rows.length > 0) {

            //const rowId = selectedRanges[0].rows[0].rowId;

            const selectedSolventIdCell = selectedRanges[0].first.row.cells[SOLVENT_ID_COLUMN_IDX] as any;
            const opticalRotationDatumId = selectedSolventIdCell.text;

            setSelectedOpticalRotationDatumId(opticalRotationDatumId);
        }

        return true;
    }

    function handleChanges(changes: CellChange<TextCell>[]) {

        //maybe rework this - 
        //   applyChangesToWeightData set to variable, then pass that to calculateValues
        //   then setWeightData  :-)
        if (!opticalRotationData)
            return;

        let prevOpticalRotationData = [...opticalRotationData]

        let editedOpticalRotationData = applyChangesToWeightData(changes, prevOpticalRotationData);

        //let calculatedWeightData = calculateValues(editedWeightData);

        setOpticalRotationData(editedOpticalRotationData);

        //setWeightData((prevWeightData) => applyChangesToWeightData(changes, prevWeightData));
    };

    function applyChangesToWeightData(changes: CellChange<TextCell>[], prevOpticalRotationData: OpticalRotationDatum[]): OpticalRotationDatum[] {
        //console.log("changes:");
        //console.log(changes);

        changes.forEach((change) => {
            const opticalRotationIndex = change.rowId;
            const fieldName = change.columnId;

            prevOpticalRotationData[opticalRotationIndex][fieldName] = change.newCell.text;

            if (prevOpticalRotationData[opticalRotationIndex].editType !== EditType.Insert)
                prevOpticalRotationData[opticalRotationIndex].editType = EditType.Update;
        });

        return [...prevOpticalRotationData];
    };

    function getRows() {
        //console.log("getRows");
        //console.log("samples...");
        //console.log(samples);



        let createdRows: Row[];

        if (!opticalRotationData) {
            let newOpticalRotationData: OpticalRotationDatum[] = [];
            newOpticalRotationData.push({ opticalRotationDatumId: new Guid().toString(), sampleName: "", editType: EditType.Insert });

            setOpticalRotationData(newOpticalRotationData);
        }
        else {
            //if (weightData) {
            //if (editedWeightData) {
            //let editedWeightData = [...weightData];

            //editedWeightData = calculateValues(editedWeightData);

            //setWeightData(editedWeightData);


            createdRows = [
                headerRow,
                //...weightData.map<Row<DefaultCellTypes>>((weightDatum, idx) => ({
                ...opticalRotationData.map<Row<DefaultCellTypes>>((opticalRotationDatum, idx) => ({
                    rowId: idx,
                    cells: [
                        { type: "text", text: opticalRotationDatum.opticalRotationDatumId ? opticalRotationDatum.opticalRotationDatumId : "", nonEditable: true, className: "reactgrid-hidden-column" },
                        { type: "text", text: "", nonEditable: true, className: getEditTypeTemplate(opticalRotationDatum) },
                        { type: "text", text: opticalRotationDatum.sampleName ? opticalRotationDatum.sampleName : "", className: "reactgrid-cell" },
                        { type: "text", text: opticalRotationDatum.lotNumber ? opticalRotationDatum.lotNumber : "", className: "reactgrid-cell" },
                        { type: "text", text: formatDecimalValue(opticalRotationDatum.weight), className: "reactgrid-cell" },
                        { type: "text", text: formatDecimalValue(opticalRotationDatum.correctedWeight), className: "reactgrid-cell" },
                        { type: "text", text: formatDecimalValue(opticalRotationDatum.concentration), className: "reactgrid-cell" },
                        { type: "text", text: formatDecimalValue(opticalRotationDatum.rotation), className: "reactgrid-cell" }
                        //{ type: "text", text: formatIntegerValue(opticalRotationDatum.angularRotation), className: "reactgrid-cell" }
                    ]
                }))
            ];

            setRows(createdRows);
        }
    }

    function formatDecimalValue(tempValue: any) {
        if (!tempValue)
            return "0.000";

        const temp = parseFloat(tempValue);

        if (!temp)
            return "0.000";

        return temp.toFixed(DECIMAL_PLACES).toString();
    }

    function formatIntegerValue(tempValue: any) {
        if (!tempValue)
            return "0";

        const parseIntegerValue = parseInt(tempValue);

        if (!parseIntegerValue)
            return "0";

        return parseIntegerValue.toString();
    }

    //render

    function getEditTypeTemplate(opticalRotationDatum: OpticalRotationDatum) {
        switch (opticalRotationDatum.editType) {
            case EditType.None:
                return "";
            case EditType.Insert:
                return "reactgrid-add-icon";
            case EditType.Update:
                return "reactgrid-edit-icon";
            case EditType.Delete:
                return "reactgrid-delete-icon";
        }
    }

    function onSaveClick() {
        saveOpticalRotationData();
    }

    function onCloseClick() {
        if (opticalRotationData) {

            const anyNonSavedChanges = opticalRotationData.some(a => a.editType !== EditType.None);

            if (anyNonSavedChanges) {
                showPromptMessage(ModalMessageType.PromptYesNo, "Pending Changes Not Saved", "You have pending changes not yet saved. Do you want to close anyway?", PENDING_CHANGES);
            }
            else
                closeDialog();
        }

        closeDialog();
    }

    async function closeDialog() {
        if (props.onClose)
            props.onClose();
    }

    async function saveOpticalRotationData() {
        if (!opticalRotationData)
            return;

        let preparedOpticalRotationData = [...opticalRotationData];

        preparedOpticalRotationData.forEach(d => {
            d.benchSheetId = props.benchSheetId;
        });

        console.log("preparedOpticalRotationData...");
        console.log(preparedOpticalRotationData);

        if (preparedOpticalRotationData) {
            await updateOpticalRotationData(preparedOpticalRotationData)
                .then(() => {
                    //showToast("Readings Saved", "success");
                    getOpticalRotationData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Optical Rotation Data");
                });
        }
    }

    async function updateOpticalRotationData(preparedOpticalRotationData: OpticalRotationDatum[]) {
        return await CrudFunctions.putData("benchsheets/opticalrotationdata", preparedOpticalRotationData);
    }

    // CRUD functions

    async function getOpticalRotationData() {
        await CrudFunctions.getData(`benchsheets/opticalrotationdata/${props.benchSheetId}`)
            .then(data => {
                console.log("OpticalRotationData:");
                console.log(data);

                setOpticalRotationData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving OPtical Rotation Data");
            });
    }

    async function getAllData() {
        assembleGrid();

        if (props.benchSheetId) {
            await Promise.all([
                getOpticalRotationData()
            ]);
        }
    }

    function assembleGrid() {
        getInitColumns();
        getHeaderRow();
    }

    function getInitColumns() {
        let initColumns: Column[] = [];

        initColumns.push({ columnId: "benchSheetWeightDataId", width: -20 });
        initColumns.push({ columnId: EDIT_TYPE_COLUMN_ID, width: -20 });
        initColumns.push({ columnId: "sampleName", width: 110 });
        initColumns.push({ columnId: "lotNumber", width: 110, resizable: true });
        initColumns.push({ columnId: "weight", width: 125, resizable: true });
        initColumns.push({ columnId: "correctedWeight", width: 100, resizable: true });
        initColumns.push({ columnId: "concentration", width: 125, resizable: true });
        initColumns.push({ columnId: "rotation", width: 125, resizable: true });
        //initColumns.push({ columnId: "angularRotation", width: 125, resizable: true });

        setColumns(initColumns);
    }

    function getHeaderRow() {
        let headerCells: DefaultCellTypes[] = [];

        headerCells.push({ type: "header", text: "", className: "reactgrid-hidden-column" });
        headerCells.push({ type: "header", text: "", className: "" });
        headerCells.push({ type: "header", text: "Sample Name", className: "reactgrid-header-cell" });
        headerCells.push({ type: "header", text: "Lot#", className: "reactgrid-header-cell" });
        headerCells.push({ type: "header", text: "Weight", className: "reactgrid-header-cell" });
        headerCells.push({ type: "header", text: "Corrected Weight", className: "reactgrid-header-cell" });
        headerCells.push({ type: "header", text: "Concentration", className: "reactgrid-header-cell" });
        headerCells.push({ type: "header", text: "Rotation(°)", className: "reactgrid-header-cell" });
       // headerCells.push({ type: "header", text: "Ang. Rotation(°)", className: "reactgrid-header-cell" });

        const initHeaderRow: Row = {
            rowId: "header",
            cells: headerCells
        };

        setHeaderRow(initHeaderRow);
    }

    function onGlobalDialogReply(reply: MessageBoxReply) {
        console.log("onGlobalDialogReply");
        console.log(reply);

        if (reply.code === PENDING_CHANGES) {
            if (reply.reply !== MessageBoxReplyType.Yes) {
                setGlobalDialogVisible(false);
            }
            else {
                closeDialog();
            }
        }
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showPromptMessage(mode: ModalMessageType, title: string, message: string, code: number) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message,
            code: code
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

